import { useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react'
import { useNavigate, Link } from 'react-router-dom'

import { useAppContext } from '@/main/app/context'
import { useAppDispatch } from '@/main/app/hooks/storeHooks'
import { listFormSaved } from '@/main/features/List/listSlice'
import { DatesAndShippingSchema, DatesAndShipping, Dates } from '@/main/app/schema'

import { useListOutletContext } from '@/cart/hooks/useListOutletContext'

import { Button } from '@/main/components/Button/Button'

import { RentalDatePicker } from './RentalDatePicker'
import { DeliveryFields } from './DeliveryFields'

import { fieldsetClassList, labelClassList, legendClassList } from '@/main/app/utils/classNames'

export function DateAndShippingForm() {
  const {
    formPaths: [step1, , step3],
  } = useAppContext()

  const { id, form } = useListOutletContext()

  const defaultValues = {
    dates: form?.dates?.values,
    shipping: form?.shipping?.values,
  }

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const methods = useForm<DatesAndShipping>({
    defaultValues,
    resolver: zodResolver(DatesAndShippingSchema),
  })

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setError,
    formState: { errors },
  } = methods

  const shippingMethod = watch('shipping.shippingMethod')

  const onSubmit = (data: DatesAndShipping) => {
    dispatch(listFormSaved(id, data))
    navigate(`/${id}/form/${step3}`)
  }

  useEffect(() => {
    const dates = getValues('dates')

    if (
      (dates.startDate !== undefined && dates.startDate.length > 0) ||
      (dates.endDate !== undefined && dates.endDate.length > 0)
    ) {
      const initialDates = Dates.safeParse(dates)

      if (!initialDates.success) {
        const datesErrors = initialDates.error.format()
        const startDateError = errors.dates?.startDate
        const endDateError = errors.dates?.endDate

        if (datesErrors.startDate !== undefined && startDateError === undefined) {
          setError('dates.startDate', {
            type: 'custom',
            message: datesErrors.startDate._errors[0],
          })
        }

        if (datesErrors.endDate !== undefined && endDateError === undefined) {
          setError('dates.endDate', {
            type: 'custom',
            message: datesErrors.endDate._errors[0],
          })
        }
      }
    }
  }, [errors.dates, setError, getValues])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h2 className="twc-font-medium">2. Dates and Pick-up/Delivery</h2>

        <p className="twc-prose-sm twc-text-rose-700 twc-mb-6">All fields are required.</p>

        <h3>Rental Dates</h3>
        <fieldset
          className={`${fieldsetClassList} twc-mb-8`}
          aria-invalid={errors.dates?.endDate || errors.dates?.startDate ? 'true' : 'false'}
        >
          <legend className={legendClassList}>
            Choose the start date and end date for your rental. ACME is closed weekends.
          </legend>

          <RentalDatePicker />
        </fieldset>

        <h3>Pick-up/Delivery Info</h3>
        <fieldset
          className={`${fieldsetClassList} twc-mb-8 twc-pb-4`}
          role="radiogroup"
          aria-invalid={errors.shipping?.shippingMethod ? 'true' : 'false'}
          aria-describedby="shipping.shippingMethod-feedback"
        >
          <legend className={legendClassList}>Choose Pick-Up or ACME Arranged Courier</legend>

          <div className="twc-flex twc-flex-col twc-gap-4 twc-mb-2">
            <label className={`${labelClassList} twc-inline-flex twc-items-center twc-gap-2`}>
              <input
                className="twc-form-radio twc-peer"
                type="radio"
                id="pickup"
                value="pickup"
                {...register('shipping.shippingMethod', { required: true })}
              />{' '}
              <span className="twc-text-base peer-checked:twc-font-bold peer-checked:twc-text-blue-800 dark:peer-checked:twc-text-blue-300">
                I&apos;ll pickup and return myself to/from ACME&apos;s location
              </span>
            </label>
            <label className={`${labelClassList} twc-inline-flex twc-items-center twc-gap-2`}>
              <input
                className="twc-form-radio twc-peer"
                type="radio"
                id="delivery"
                value="delivery"
                {...register('shipping.shippingMethod', { required: true })}
              />{' '}
              <span className="twc-text-base peer-checked:twc-font-bold peer-checked:twc-text-blue-800 dark:peer-checked:twc-text-blue-300">
                I&apos;d like ACME to arrange courier delivery
              </span>
            </label>
          </div>

          {shippingMethod === 'pickup' && (
            <p className="twc-prose-sm twc-text-zinc-500/80 twc-mt-2 dark:twc-text-slate-300">
              ACME is open Monday thru Friday, 9 AM to 6 PM for client pick-up/drop-off.
            </p>
          )}

          <div id="profile.firstName-feedback" aria-live="polite">
            <ErrorMessage
              className="twc-mt-1 twc-mb-0 twc-text-xs twc-font-medium twc-text-red-600"
              errors={errors}
              name="shipping.shippingMethod"
              as="p"
            />
          </div>
        </fieldset>

        {shippingMethod === 'delivery' && <DeliveryFields />}

        <div className="twc-flex twc-flex-col twc-gap-8 twc-mb-6 twc-items-end">
          <Button type="submit">
            Save &amp; Continue to Review &amp; Submit <ArrowRight size={18} />
          </Button>

          <Link
            to={`/${id}/form/${step1}`}
            className="twc-prose-sm twc-font-normal twc-underline twc-flex twc-gap-1 twc-items-center hover:twc-text-slate-600 hover:twc-no-underline"
          >
            <ArrowLeft /> Back to Contact &amp; Billing
          </Link>
        </div>
      </form>
    </FormProvider>
  )
}
